<template>
  <OrganismsLoading v-if="!initProductsLoaded" />
  <div v-else :class="$style.products">
    <CommonBreadcrumbs :breadcrumbs="breadcrumbs" />
    <ProductAllList
      :sort="'recommendation'"
      :filter="productAllListFilter"
      :products="apiProductSearchQueryData?.products ?? []"
      :previous-search-result="previousSearchResult"
      :all-list-categories="categories"
      :filter-counts="apiProductSearchQueryData?.filterCounts"
      :pager="{
        totalPage: Math.ceil(previousSearchResult.allCount / Number(apiProductSearchQueryVars.limit)),
        currentPage: Number(apiProductSearchQueryVars.page),
      }"
      @update:filter="fetchByUpdateFilter"
      @update:sort="fetchByUpdateSort"
      @keyword-search="fetchByUpdateKeyword"
      @update-page="refetchWithUpdateQuery($event.toString())"
      @reset="resetFetch"
    />
  </div>
</template>

<script setup lang="ts">
import type { ProductAllListProps, ProductAllListProps_Filter } from "~/types/product"
import { usePublicProductCategoryAllForSiteQuery } from "~/gql/urql.generated"
import type { CommonBreadcrumbType } from "~/types/components/common"
import type { ApiProductSearchQuery, ApiProductSearchResponse } from "~/types/server/api/product"
import type { ProductAllSearchInput_Sort } from "~/const/product"
import { omitVariableKeys } from "~/utils/functions/object"
import { scrollToProductList } from "~/utils/functions/product"
definePageMeta({ layout: "default-with-extended-footer" })
const route = useRoute()

/**
 * 初回読み込み完了を判断する
 */
const initProductsLoaded = ref(false)
const queryCsv2ArrayFilter = (queryValue: string | undefined) => queryValue?.split(",").filter((v) => v !== "") ?? []

const breadcrumbs: CommonBreadcrumbType[] = [
  {
    title: "トップ",
    url: "/",
  },
  {
    title: "商品一覧",
    url: "/products",
  },
]

const { buildKeywordFromRouteQuery, PRODUCT_LIST_LIMIT } = useProductsPage()

const createVarsFromQuery = (): ApiProductSearchQuery => {
  return {
    keywords: queryCsv2ArrayFilter(route.query.keywords?.toString()).filter((v) => !!v.trim()),
    category: route.query.category?.toString() || "",
    sex: queryCsv2ArrayFilter(route.query.sex?.toString()) as ApiProductSearchQuery["sex"],
    fabric: queryCsv2ArrayFilter(route.query.fabric?.toString()) as ApiProductSearchQuery["fabric"],
    season: queryCsv2ArrayFilter(route.query.season?.toString()) as ApiProductSearchQuery["season"],
    wear_category: queryCsv2ArrayFilter(
      route.query.wear_category?.toString(),
    ) as ApiProductSearchQuery["wear_category"],
    silhouette: queryCsv2ArrayFilter(route.query.silhouette?.toString()) as ApiProductSearchQuery["silhouette"],
    size: queryCsv2ArrayFilter(route.query.size?.toString()) as ApiProductSearchQuery["size"],
    ...(route.query.is_in_stock === "1" ? { is_in_stock: "1" } : {}),
    ...(route.query.is_outlet === "1" ? { is_outlet: "1" } : {}),
    ...(route.query.campaign ? { campaign: route.query.campaign as ApiProductSearchQuery["campaign"] } : {}),
    page: route.query.page?.toString() ?? "1",
    limit: PRODUCT_LIST_LIMIT,
    sort: (route.query.sort as ProductAllSearchInput_Sort) ?? "recommendation",
  }
}

const createQuery = (page?: string) => {
  return {
    keywords: Array.isArray(apiProductSearchQueryVars.value.keywords)
      ? apiProductSearchQueryVars.value.keywords.join(",")
      : apiProductSearchQueryVars.value.keywords,
    category: apiProductSearchQueryVars.value.category,
    sex: Array.isArray(apiProductSearchQueryVars.value.sex)
      ? apiProductSearchQueryVars.value.sex.join(",")
      : apiProductSearchQueryVars.value.sex,
    fabric: Array.isArray(apiProductSearchQueryVars.value.fabric)
      ? apiProductSearchQueryVars.value.fabric.join(",")
      : apiProductSearchQueryVars.value.fabric,
    season: Array.isArray(apiProductSearchQueryVars.value.season)
      ? apiProductSearchQueryVars.value.season.join(",")
      : apiProductSearchQueryVars.value.season,
    wear_category: Array.isArray(apiProductSearchQueryVars.value.wear_category)
      ? apiProductSearchQueryVars.value.wear_category.join(",")
      : apiProductSearchQueryVars.value.wear_category,
    size: Array.isArray(apiProductSearchQueryVars.value.size)
      ? apiProductSearchQueryVars.value.size.join(",")
      : apiProductSearchQueryVars.value.size,
    silhouette: Array.isArray(apiProductSearchQueryVars.value.silhouette)
      ? apiProductSearchQueryVars.value.silhouette.join(",")
      : apiProductSearchQueryVars.value.silhouette,
    ...(apiProductSearchQueryVars.value.is_in_stock === "1" ? { is_in_stock: "1" } : {}),
    ...(apiProductSearchQueryVars.value.is_outlet === "1" ? { is_outlet: "1" } : {}),
    ...(apiProductSearchQueryVars.value.campaign ? { campaign: apiProductSearchQueryVars.value.campaign } : {}),
    page: page ?? route.query.page?.toString() ?? "1",
    limit: PRODUCT_LIST_LIMIT,
    sort: apiProductSearchQueryVars.value.sort,
  }
}

const apiProductSearchQueryVars = ref<ApiProductSearchQuery>(createVarsFromQuery())
const productAllListFilter = computed<ProductAllListProps_Filter>(() =>
  omitVariableKeys(apiProductSearchQueryVars.value, ["page", "limit", "keywords"]),
)

const { data: apiProductSearchQueryData, refresh: apiProductSearchQueryRefresh } = await useAsyncData(async () => {
  const data = await $fetch<ApiProductSearchResponse>("/api/product/search", {
    query: apiProductSearchQueryVars.value,
  })

  initProductsLoaded.value = true
  return data
})

const { executeQuery: _publicProductCategoryAllForSiteQueryExec } = await usePublicProductCategoryAllForSiteQuery({
  requestPolicy: "cache-first",
  variables: {
    page: 1,
    limit: 100,
    parent_category_ids: [
      "66099f78792c18d4d50fe312", // NightCon TODO:本番のものに変える
      "66099f78792c18d4d50fe311", // DayCon TODO:本番のものに変える
    ],
  },
  pause: true,
})

const { data: _publicProductCategoryAllForSiteQueryData } = await useAsyncData(async () => {
  const { data } = await _publicProductCategoryAllForSiteQueryExec()
  return data.value?.publicProductCategoryAllForSite?.items ?? []
})

const categories = computed(() => [
  {
    document_id: undefined,
    name: "すべて",
  },
  ...(_publicProductCategoryAllForSiteQueryData.value
    // ワークサポートの「入浴剤」を省いている
    // TODO: 表示するカテゴリを管理画面から制御可能にする必要あり
    ?.filter((record) => record.document_id !== "611fc01b8d50b8014368e475")
    ?.map((record) => ({
      document_id: record.document_id ?? "",
      name: record.name ?? "",
    })) ?? []),
])

const previousSearchResult = computed<ProductAllListProps["previousSearchResult"]>(() => {
  const previousKeyword = buildKeywordFromRouteQuery(route)

  const allCount = apiProductSearchQueryData.value?.pageInfo.allCount ?? 0
  const startZero = Number(PRODUCT_LIST_LIMIT) * (Number(apiProductSearchQueryVars.value.page) - 1)
  const end = startZero + Number(PRODUCT_LIST_LIMIT)

  return {
    keyword: previousKeyword,
    allCount,
    pageStart: startZero + 1,
    pageEnd: end > allCount ? allCount : end,
  }
})

const refetchWithUpdateQuery = async (page: string) => {
  apiProductSearchQueryVars.value.page = page
  await navigateTo({ query: createQuery(page) })

  await apiProductSearchQueryRefresh()
  scrollToProductList()
}

const fetchByUpdateKeyword = async (keyword: string) => {
  // 検索条件が変わるため、1ページ目から表示させる
  // eslint-disable-next-line no-irregular-whitespace
  await resetFetch(keyword.split(/ |　/).filter((keyword) => keyword !== ""))
}

const fetchByUpdateFilter = async (value: ProductAllListProps_Filter) => {
  // 検索条件が変わるため、1ページ目から表示させる
  apiProductSearchQueryVars.value = {
    ...apiProductSearchQueryVars.value,
    ...value,
  }
  await refetchWithUpdateQuery("1")
}

const fetchByUpdateSort = async (value: ProductAllSearchInput_Sort) => {
  apiProductSearchQueryVars.value.sort = value
  await refetchWithUpdateQuery(apiProductSearchQueryVars.value.page ?? "1")
}

const resetFetch = async (keywords?: string[], campaign?: ApiProductSearchQuery["campaign"]) => {
  apiProductSearchQueryVars.value = {
    keywords: keywords ?? [],
    page: "1",
    limit: PRODUCT_LIST_LIMIT,
  }
  if (campaign) {
    apiProductSearchQueryVars.value.campaign = campaign
  }

  await refetchWithUpdateQuery("1")
}

watch(
  () => route.fullPath,
  async () => {
    const keywords = Array.isArray(apiProductSearchQueryVars.value.keywords)
      ? apiProductSearchQueryVars.value.keywords.join(",")
      : apiProductSearchQueryVars.value.keywords
    if (keywords === route.query.keywords?.toString()) return

    // キーワード変更を検知 -> キーワード以外をリセットして検索する
    await resetFetch(queryCsv2ArrayFilter(route.query.keywords?.toString()))
  },
)
</script>

<style scoped module lang="scss">
.products {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
